import { type Bundle } from '../Bundle.ts';
import type { Product, ProductEnrichments } from '../Product.ts';

export function fromEcapiDto(bundleId: string, bundle: any): Bundle {
  return {
    productId: bundleId,
    title: bundle.title as string,
    description: bundle.description as string,
    applicableProducts: (bundle.products as string[]) ?? []
  };
}

const transformBundleContents = ([bundleId, { bundleContents }]: [
  string,
  ProductEnrichments
]) =>
  (bundleContents ?? [])
    .filter(
      ({ productId }) => productId.toLowerCase() !== bundleId.toLowerCase() // Remove self-assigned bundles
    )
    .map(({ productId }) => [productId, bundleId]);

/**
 * Map product enrichments bundleContents product ID to associated bundle IDs
 * e.g.
 * productId1: => [bundleId1, bundleId2]
 * productId2: => [bundleId2, bundleId3]
 */
export function mapAssociatedBundles(
  productEnrichments: Record<string, ProductEnrichments>
): Map<string, string[]> {
  return Object.entries(productEnrichments)
    .flatMap(transformBundleContents)
    .reduce((accum, [productId, bundleId]: string[]) => {
      accum.set(productId, (accum.get(productId) ?? []).concat(bundleId));
      return accum;
    }, new Map<string, string[]>());
}

/**
 * For a given product ID, populate mapped associatedBundle IDs with Products
 */
export function getAssociatedBundlesByProductId(
  productId: string,
  bundlesMap: Map<string, string[]>,
  products: Product[]
): Product[] {
  const bundleIds = bundlesMap.get(productId) ?? [];

  if (bundleIds.length === 0) return [];

  const productById = new Map<string, Product>(
    products.map((product) => [product.id, product])
  );

  return bundleIds.map((id) => productById.get(id)!).filter(Boolean);
}
